import { IContent, ILesson } from 'interface/lesson';
import { isStatusNegative, dateFormater, haveTermsBeenAccepted } from 'utils';
import Player from './Player';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CoverImage from './CoverImage';
import LoginBanner from 'components/Course/LoginBanner';
import { useAppSelector } from 'hooks/redux';
import { useEffect } from 'react';
import nothingToShowImage from 'assets/img/nocomments.png';
import Typography from '@material-ui/core/Typography';
import { useHistory } from 'react-router-dom';

interface IContentProps {
  lesson: ILesson;
  courseHash: string;
  courseId: number;
}
const useStyles = makeStyles((theme) => ({
  fullContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 20,
  },
  player: {
    width: '100%',
    minHeight: 500,
    maxWidth: 1280,
    marginTop: 48,
    overflow: 'hidden',
  },
  waitingContainer: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(0, 6),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(10),
      marginBottom: theme.spacing(10),
    },
  },
  noLessonTitle: {
    color: theme.palette.grey[500],
    fontWeight: 'bold',
    fontSize: 24,
    marginTop: theme.spacing(2),
    maxWidth: 650,
  },
  imgWrapper: {
    width: 400,
    height: 350,
    marginTop: theme.spacing(10),
    position: 'relative',
    '@media (max-width: 640px)': {
      transform: 'scale(0.7)',
      marginBottom: theme.spacing(-5),
      marginTop: theme.spacing(-5),
    },
  },
}));

const ContentArea = ({ lesson, courseHash, courseId }: IContentProps) => {
  const termsNotAccepted = haveTermsBeenAccepted(lesson.status);
  const history = useHistory();

  if (termsNotAccepted) {
    history.push(`/curso/${courseHash}`);
  }

  const classes = useStyles();
  const lessonPageCount = useAppSelector((state) => state.user.lessonPageCount);
  const hasNotAllowed = isStatusNegative(lesson.status) || !lesson.contents || !lesson.contents.length;

  useEffect(() => window.scrollTo(0, 0));

  const lessonStatusMessage = {
    not_released: `Essa aula será liberada no dia ${dateFormater(lesson.release)}`,
    expired: 'Parece que você não possui acesso a essa aula, pois ela expirou.',
    dont_have: 'Parece que você não possui acesso a essa aula.',
  };

  const listTypeStatusMessage = new Map(Object.entries(lessonStatusMessage));

  const fallBackContent = {
    multiple: {
      auth: true,
      message:
        'Parece que você está acessando o Nutror em outros dispositivos. Não é possível assistir uma aula se você estiver em mais de um disposítivo ao mesmo tempo',
      route: '/perfil',
      textButton: 'Alterar sua senha',
    },
    invalidStatus: {
      auth: true,
      message: listTypeStatusMessage.has(lesson.status)
        ? lessonStatusMessage[lesson.status]
        : lessonStatusMessage['dont_have'],
      route: `/curso/${courseHash}`,
      textButton: 'Volte para o curso',
    },
  };

  const hasInvalidStatusLesson = isStatusNegative(lesson.status);
  const hasMultipleAccess = !lesson.allow_multiple_access && lessonPageCount > 1;
  const showConverImage = lesson?.contents?.length === 1;
  const createMultipleContents = () => {
    return lesson.contents.map((content: IContent, index: number) => {
      return (
        <Player
          courseId={courseId}
          key={content.id || index}
          drmActive={content?.drm_active}
          embed={content?.embed}
          type={content.type !== undefined ? content.type : { id: 0 }}
          status={lesson.status}
          courseHash={courseHash}
          content={content}
        />
      );
    });
  };

  const players = hasNotAllowed ? (
    <Player
      key={0}
      drmActive={0}
      embed={null}
      type={{ id: 0 }}
      status={lesson.status}
      courseHash={courseHash}
      courseId={courseId}
    />
  ) : (
    createMultipleContents()
  );

  const contentWithAlert = (status: string) => (
    <LoginBanner
      isAuth={fallBackContent[status].auth}
      message={fallBackContent[status].message}
      route={fallBackContent[status].route}
      textButton={fallBackContent[status].textButton}
    />
  );

  const playerContent = (
    <>
      {showConverImage && <CoverImage type={lesson?.contents[0]?.type?.id} />}
      {players}
    </>
  );

  const emptyContent = (
    <>
      <div className={classes.waitingContainer}>
        <div className={classes.imgWrapper}>
          <img src={nothingToShowImage} alt='Vitrine vazia' />
        </div>
        <Typography align='center' className={classes.noLessonTitle}>
          Hum! Parece que esta aula ainda não possui nenhum conteúdo.
        </Typography>
      </div>
    </>
  );

  const renderPlayer = () => {
    if (Array.isArray(lesson.contents) && lesson.contents.length <= 0) {
      return emptyContent;
    }
    if (hasInvalidStatusLesson) {
      return contentWithAlert('invalidStatus');
    }
    if (hasMultipleAccess) {
      return contentWithAlert('multiple');
    }
    return playerContent;
  };

  return (
    <article className={classes.fullContainer}>
      <section className={classes.player}>{renderPlayer()}</section>
    </article>
  );
};

export default ContentArea;
